import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { useMemo, useEffect } from "react"
import { toast } from "sonner"
import { z } from "zod"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  getStoreWithConfigurations,
  saveStoreConfigurationVersion,
} from "@/features/project-editor/server/projectServer"
import {
  StoreDataForm,
  type StoreConfigurationFormValues,
} from "@/features/store-configuration/components/StoreDataForm"
import { buildStoreMetadata } from "@/features/store-configuration/core/utils/storeMetadata"
import { storeConfigurationInputSchema } from "@/features/stores/storeConfigurationSchemas"
import { usePermissions } from "@/hooks/usePermissions"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

const Params = z.object({
  storeId: z.string(),
})

const getInitialData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator(Params)
  .handler(async ({ data }) => {
    return await getStoreWithConfigurations({ data })
  })

export const Route = createFileRoute(
  "/(authenticated)/settings/stores/$storeId/configurations/new",
)({
  params: Params,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(queryKey(params))
  },
  component: NewConfigurationPage,
})

const queryKey = (params: z.infer<typeof Params>) => ({
  queryKey: ["store-config-new", params.storeId],
  queryFn: () => getInitialData({ data: params }),
})

function NewConfigurationPage() {
  const params = Route.useParams()
  const navigate = useNavigate()
  const { data } = useQuery(queryKey(params))
  const queryClient = useQueryClient()
  const { hasPermission, isLoading: permissionsLoading } = usePermissions()
  const canCreate = hasPermission("storeVersion", "create").allowed

  // Redirect if user doesn't have permission to create store versions
  useEffect(() => {
    if (!permissionsLoading && !canCreate) {
      toast.error(t`You do not have permission to create configurations`)
      void navigate({
        to: "/settings/stores/$storeId",
        params: { storeId: params.storeId },
      })
    }
  }, [permissionsLoading, canCreate, navigate, params.storeId])

  const initialMetadata = useMemo(
    () =>
      data ? buildStoreMetadata(data.configurations[0] ?? null, data.metadataExtension) : undefined,
    [data],
  )

  const defaultConfigurationName = data ? data.store.name : undefined
  const defaultValidFrom = new Date().toISOString().slice(0, 10)

  const saveMutation = useMutation({
    mutationFn: async (formData: StoreConfigurationFormValues) => {
      const parsed = storeConfigurationInputSchema.parse({
        ...formData,
        storeId: params.storeId,
      })
      await saveStoreConfigurationVersion({ data: parsed })
    },
    onSuccess: () => {
      toast.success(t`Configuration saved`)
      void queryClient.invalidateQueries({
        queryKey: ["store-detail", params.storeId],
      })
      void navigate({
        to: "/settings/stores/$storeId",
        params: { storeId: params.storeId },
      })
    },
    onError: () => toast.error(t`Failed to save configuration`),
  })

  // Don't render until permissions are loaded and user has permission
  if (permissionsLoading || !canCreate) {
    return null
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
            <Trans>Store configuration</Trans>
          </div>
          <h1 className="font-serif text-4xl font-bold text-neutral-900 dark:text-white">
            <Trans>New configuration</Trans>
          </h1>
        </div>
        <Link
          to="/settings/stores/$storeId"
          params={{ storeId: params.storeId }}
          className="text-sm text-blue-600 hover:underline"
        >
          <Trans>Back to store</Trans>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>Create a new store configuration</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <StoreDataForm
            initialData={initialMetadata}
            onSave={(values) => saveMutation.mutate(values)}
            isLoading={saveMutation.isPending}
            defaultConfigurationName={defaultConfigurationName}
            defaultValidFrom={defaultValidFrom}
          />
        </CardContent>
      </Card>
    </div>
  )
}
